import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { confirmDialog } from '../../lib/confirmDialog';
import { validatePortalUrl } from '../../lib/urlValidator';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * LinksSectionBuilder - Manage portal links for a team
 * Allows adding, editing, deleting, and reordering links
 */
export default function LinksSectionBuilder({ teamId, sectionId, links = [], onLinksUpdate, addTrigger = 0 }) {
  console.log('[LinksSectionBuilder] Rendered with:', { 
    teamId, 
    sectionId, 
    totalLinks: links.length,
    allLinks: links 
  });
  
  // Filter links to only show links for this specific section
  const sectionLinks = links.filter(link => link.section_id === sectionId);
  console.log('[LinksSectionBuilder] Filtered to section links:', { 
    sectionId, 
    filteredCount: sectionLinks.length,
    sectionLinks 
  });
  
  const [localLinks, setLocalLinks] = useState(sectionLinks);
  const [editingId, setEditingId] = useState(null);
  const [newLink, setNewLink] = useState({
    title: '',
    description: '',
    url: ''
  });
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const canMutate = Boolean(teamId && sectionId);

  useEffect(() => {
    console.log('[LinksSectionBuilder] useEffect triggered, updating localLinks');
    setLocalLinks(sectionLinks);
  }, [links, sectionId]);

  useEffect(() => {
    if (addTrigger === 0 || !canMutate) return;
    setIsAdding(true);
  }, [addTrigger, canMutate]);

  const handleAddLink = async () => {
    console.log('[LinksSectionBuilder] handleAddLink called:', { newLink, teamId, sectionId, localLinksCount: localLinks.length });
    
    if (!canMutate) {
      toast.error('Please save the section before adding links.');
      return;
    }

    if (!newLink.title.trim() || !newLink.url.trim()) {
      console.error('[LinksSectionBuilder] Validation failed: missing title or URL');
      toast.error('Title and URL are required');
      return;
    }

    const { valid, normalizedUrl, error: urlError } = validatePortalUrl(newLink.url);
    if (!valid) {
      console.error('[LinksSectionBuilder] URL validation failed:', newLink.url, urlError);
      toast.error(urlError || 'Please enter a valid URL');
      return;
    }

    const insertData = {
      team_id: teamId,
      section_id: sectionId,
      title: newLink.title.trim(),
      description: newLink.description.trim() || null,
      url: normalizedUrl,
      sort_order: localLinks.length,
      visible: true
    };
    console.log('[LinksSectionBuilder] Inserting link:', insertData);

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('portal_links')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('[LinksSectionBuilder] Insert error:', error);
        throw error;
      }

      console.log('[LinksSectionBuilder] Link inserted successfully:', data);
      const updatedLinks = [...localLinks, data];
      console.log('[LinksSectionBuilder] Updated links array:', updatedLinks);
      setLocalLinks(updatedLinks);
      onLinksUpdate?.(sectionId, updatedLinks);
      
      // Reset form
      setNewLink({ title: '', description: '', url: '' });
      setIsAdding(false);
      toast.success('Link added successfully');
    } catch (error) {
      console.error('[LinksSectionBuilder] Error adding link:', error);
      toast.error('Failed to add link: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLink = async (linkId, updates) => {
    console.log('[LinksSectionBuilder] handleUpdateLink called:', { linkId, updates, currentLocalLinks: localLinks });
    
    // Validate URL if it's being updated
    if (updates.url) {
      const { valid, normalizedUrl, error: urlError } = validatePortalUrl(updates.url);
      if (!valid) {
        console.error('[LinksSectionBuilder] URL validation failed during update:', updates.url, urlError);
        toast.error(urlError || 'Please enter a valid URL');
        return;
      }
      updates.url = normalizedUrl;
      console.log('[LinksSectionBuilder] URL validated:', normalizedUrl);
    }

    console.log('[LinksSectionBuilder] Updating link in database:', { linkId, updates });
    setSaving(true);
    try {
      const { error } = await supabase
        .from('portal_links')
        .update(updates)
        .eq('id', linkId);

      if (error) {
        console.error('[LinksSectionBuilder] Update error:', error);
        throw error;
      }

      console.log('[LinksSectionBuilder] Link updated in database successfully');
      const updatedLinks = localLinks.map(link =>
        link.id === linkId ? { ...link, ...updates } : link
      );
      console.log('[LinksSectionBuilder] Updated local links array:', updatedLinks);
      setLocalLinks(updatedLinks);
      onLinksUpdate?.(sectionId, updatedLinks);
      setEditingId(null);
      toast.success('Link updated successfully');
    } catch (error) {
      console.error('[LinksSectionBuilder] Error updating link:', error);
      toast.error('Failed to update link: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    console.log('[LinksSectionBuilder] handleDeleteLink called:', { linkId, currentLocalLinks: localLinks });
    
    const confirmed = await confirmDialog(
      'Delete this link?',
      { confirmText: 'Delete', cancelText: 'Cancel' }
    );

    if (!confirmed) {
      console.log('[LinksSectionBuilder] Delete cancelled by user');
      return;
    }

    console.log('[LinksSectionBuilder] Deleting link from database:', linkId);
    setSaving(true);
    try {
      const { error } = await supabase
        .from('portal_links')
        .delete()
        .eq('id', linkId);

      if (error) {
        console.error('[LinksSectionBuilder] Delete error:', error);
        throw error;
      }

      console.log('[LinksSectionBuilder] Link deleted from database successfully');
      const updatedLinks = localLinks.filter(link => link.id !== linkId);
      console.log('[LinksSectionBuilder] Updated local links array after delete:', updatedLinks);
      setLocalLinks(updatedLinks);
      onLinksUpdate?.(sectionId, updatedLinks);
      toast.success('Link deleted successfully');
    } catch (error) {
      console.error('[LinksSectionBuilder] Error deleting link:', error);
      toast.error('Failed to delete link: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (linkId, currentVisibility) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('portal_links')
        .update({ visible: !currentVisibility })
        .eq('id', linkId);

      if (error) throw error;

      const updatedLinks = localLinks.map(link =>
        link.id === linkId ? { ...link, visible: !currentVisibility } : link
      );
      setLocalLinks(updatedLinks);
      onLinksUpdate(updatedLinks);
      toast.success(`Link ${!currentVisibility ? 'shown' : 'hidden'} on portal`);
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to update visibility: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (activeId, overId) => {
    if (activeId === overId) return;

    const fromIndex = localLinks.findIndex((link) => link.id === activeId);
    const toIndex = localLinks.findIndex((link) => link.id === overId);

    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...localLinks];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    // Update sort_order for all links
    const updates = reordered.map((link, idx) => ({ ...link, sort_order: idx }));
    setLocalLinks(updates);

    // Save to database
    setSaving(true);
    try {
      for (const link of updates) {
        await supabase
          .from('portal_links')
          .update({ sort_order: link.sort_order })
          .eq('id', link.id);
      }
      onLinksUpdate?.(sectionId, updates);
    } catch (error) {
      console.error('Error reordering links:', error);
      toast.error('Failed to reorder links');
      setLocalLinks(localLinks); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over) return;
    handleReorder(active.id, over.id);
  };

  return (
    <div className="space-y-3" aria-busy={saving}>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-gray-700">
          Portal Links
        </label>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            + Add Link
          </button>
        )}
      </div>

      {/* Add new link form */}
      {isAdding && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
          <input
            type="text"
            placeholder="Link Title *"
            value={newLink.title}
            onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="URL (e.g., https://example.com or example.com) *"
            value={newLink.url}
            onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <textarea
            placeholder="Description (optional)"
            value={newLink.description}
            onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
            rows={2}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddLink}
              disabled={saving || !canMutate}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Adding...' : 'Add Link'}
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setNewLink({ title: '', description: '', url: '' });
              }}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Existing links */}
      {localLinks.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          No links added yet. Click "Add Link" to create one.
        </p>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={localLinks.map((link) => link.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {localLinks.map((link) => (
                <SortableLinkCard
                  key={link.id}
                  link={link}
                  className={`border rounded-lg p-3 ${
                    link.visible ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300'
                  }`}
                >
                  {({ attributes, listeners }) => (
                    editingId === link.id ? (
                      <EditLinkForm
                        link={link}
                        onSave={(updates) => handleUpdateLink(link.id, updates)}
                        onCancel={() => setEditingId(null)}
                        saving={saving}
                      />
                    ) : (
                      <div>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start gap-2 flex-1">
                            <button
                              {...listeners}
                              {...attributes}
                              className="px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 rounded"
                              title="Drag to reorder"
                              aria-label="Drag link to reorder"
                              type="button"
                            >
                              ☰
                            </button>
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 text-sm">{link.title}</h4>
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline break-all"
                              >
                                {link.title}
                              </a>
                              {link.description && (
                                <p className="text-xs text-gray-600 mt-1">{link.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setEditingId(link.id)}
                            disabled={saving}
                            className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleVisibility(link.id, link.visible)}
                            disabled={saving}
                            className={`text-xs px-2 py-1 rounded ${
                              link.visible
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            } disabled:opacity-50`}
                          >
                            {link.visible ? '✓ Visible' : '✕ Hidden'}
                          </button>
                          <button
                            onClick={() => handleDeleteLink(link.id)}
                            disabled={saving}
                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </SortableLinkCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <p className="text-xs text-gray-500">
        Add important links that will be displayed on your portal. Links are shown in the order listed above.
      </p>
    </div>
  );
}

function SortableLinkCard({ link, className, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: link.id });
  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
      cursor: isDragging ? 'grabbing' : 'grab'
    }),
    [transform, transition, isDragging]
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${className}${isDragging ? ' shadow-lg z-10' : ''}`}
    >
      {children({ attributes, listeners })}
    </div>
  );
}

function EditLinkForm({ link, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(link.title);
  const [url, setUrl] = useState(link.url);
  const [description, setDescription] = useState(link.description || '');

  const handleSave = () => {
    if (!title.trim() || !url.trim()) {
      toast.error('Title and URL are required');
      return;
    }

    onSave({
      title: title.trim(),
      url: url.trim(),
      description: description.trim() || null
    });
  };

  return (
    <div className="space-y-2">
      <input
        type="text"
        placeholder="Link Title *"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <input
        type="text"
        placeholder="URL *"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <textarea
        placeholder="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

